import styled from 'styled-components';
import React, { useState, useMemo } from 'react';

import type DashboardInformationInterface from '@this/domain/dashboard_information_interface';
import InformationTypeArea from '@this/components/reserve_trip/dashboard/information_area/information_type_area';
import { ORGANIZATION_INFORMATIONS_SECTION_ID } from '@this/components/reserve_trip/dashboard/organization_information_notice/organization_information_notice';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { isTabikobo } from '@this/src/util';

const TOTAL_HEIGHT = 500;
const MARGIN = 10;
const MAX_ORGANIZATION_HEIGHT = TOTAL_HEIGHT / 2;

type Props = {
  infos: DashboardInformationInterface[];
  loading: boolean;
  serviceId: number;
};

const InformationArea: React.FC<Props> = ({ infos, loading, serviceId }) => {
  const { adminInfos, organizationInfos, hasOrgInfos } = useMemo(() => {
    const filteredAdminInfos = infos.filter(info => info.type === 'admin');
    const filteredOrgInfos = infos.filter(info => info.type === 'organization');
    return {
      adminInfos: filteredAdminInfos,
      organizationInfos: filteredOrgInfos,
      hasOrgInfos: filteredOrgInfos.length > 0
    };
  }, [infos]);

  const [organizationHeight, setOrganizationHeight] = useState<number>(0);

  const informationTitle = isTabikobo(serviceId) ? 'システムからのお知らせ' : 'AI Travelからのお知らせ';

  const adminHeight = TOTAL_HEIGHT - (hasOrgInfos ? organizationHeight : 0);

  const handleHeightChange = (height: number) => {
    setOrganizationHeight(Math.min(height + MARGIN, MAX_ORGANIZATION_HEIGHT));
  };

  return (
    <Wrapper id={ORGANIZATION_INFORMATIONS_SECTION_ID}>
      {loading && (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      )}

      {hasOrgInfos && (
        <InformationTypeArea
          infos={organizationInfos}
          title="社内通知"
          height={organizationHeight}
          onHeightChange={handleHeightChange}
        />
      )}

      <InformationTypeArea infos={adminInfos} title={informationTitle} height={adminHeight} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 430px;
  margin: 0 0 0 10px;
  font-weight: bold;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media screen and (max-width: 1030px) {
    max-width: 530px;
    width: 100%;
    margin: 0 auto;
  }

  @media screen and (max-width: 430px) {
    padding: 0 20px 20px;
    color: ${props => props.theme.textColor};
  }
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 2;
`;

export default InformationArea;
